import React, { useState } from 'react';
import PropTypes from 'prop-types';

const formatDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? null
    : parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// 12-hour clock from a stored "HH:mm[:ss]". Anything that is not a clock value is
// left out rather than printed raw.
const formatTime = (value) => {
  const match = String(value ?? '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  if (Number.isNaN(hour)) return null;
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  hour %= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${match[2]} ${meridiem}`;
};

const formatTiming = (batch) => {
  const start = formatTime(batch?.startTime);
  const end = formatTime(batch?.endTime);
  return start && end ? `${start} – ${end}` : null;
};

/**
 * Picks the batch a pending order is waiting on. Styled after CouponModal — the app
 * has no dialog or select primitive, so the sheet, the list and the selected state
 * are all hand-rolled Tailwind.
 *
 * Confirming here also confirms the order, which is why the action says so plainly:
 * the student should not discover that after the fact.
 */
function BatchModal({
  isOpen, batches, loading, submitting, onClose, onConfirm,
}) {
  const [selectedBatchId, setSelectedBatchId] = useState(null);

  if (!isOpen) return null;

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!submitting) onClose();
  };

  return (
    <div className="fixed inset-0 flex items-end md:items-center justify-center z-50">
      <div
        className="bg-black bg-opacity-50 absolute inset-0 animate-fadeIn delay-100"
        onClick={handleClose}
        tabIndex={0}
        onKeyDown={() => {}}
        role="button"
        aria-label="Close modal"
      />

      <div
        className="bg-white rounded-t-2xl md:rounded w-full md:max-w-[50%] max-w-[100%] p-4 relative z-10 animate-slideUp"
        onClick={(e) => e.stopPropagation()}
        tabIndex={0}
        onKeyDown={() => {}}
        role="button"
      >
        <div className="md: hidden flex justify-center mb-4">
          <div className="w-16 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="md:p-0 pb-6">
          <h2 className="text-xl font-bold mb-1">Select your batch</h2>
          <p className="text-[13px] text-[#64748b] mb-4">
            Your order is on hold until a batch is chosen. Confirming a batch confirms
            the order.
          </p>

          {loading && (
            <p className="text-[14px] text-[#64748b] py-6 text-center">Loading batches…</p>
          )}

          {!loading && !batches.length && (
            <p className="text-[14px] text-[#64748b] py-6 text-center">
              No batches are open for enrolment right now. Please contact the centre.
            </p>
          )}

          {!loading && batches.length > 0 && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {batches.map((batch) => {
                const isSelected = selectedBatchId === batch.id;
                const timing = formatTiming(batch);
                const startsOn = formatDate(batch.commencementDate);
                return (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                  <div
                    key={batch.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedBatchId(batch.id)}
                    className={`border rounded-lg p-3 cursor-pointer ${
                      isSelected ? 'border-[#045692] ring-1 ring-[#045692] bg-[#eff6ff]' : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-[#0f172a]">{batch.name}</span>
                      {batch.batchCode && (
                        <span className="text-[12px] text-[#64748b]">{batch.batchCode}</span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 text-[13px] text-[#64748b]">
                      {startsOn && <span>{`Starts ${startsOn}`}</span>}
                      {timing && <span>{timing}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="flex-1 border border-[#045692] rounded-lg py-3 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(selectedBatchId)}
              disabled={!selectedBatchId || submitting}
              className="flex-1 bg-[#2563eb] text-white rounded-lg py-3 font-medium disabled:opacity-50"
            >
              {submitting ? 'Confirming…' : 'Confirm batch'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

BatchModal.propTypes = {
  isOpen: PropTypes.bool,
  batches: PropTypes.array,
  loading: PropTypes.bool,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

BatchModal.defaultProps = {
  isOpen: false,
  batches: [],
  loading: false,
  submitting: false,
};

export default BatchModal;
